import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export default function BookingConfirmed() {
  const q = new URLSearchParams(useLocation().search);
  const amount = q.get("amount") || "—";
  const currency = q.get("currency") || "AED";
  const provider = q.get("provider") || "BNPL";
  const leadId = q.get("leadId");
  const offerId = q.get("offerId") || q.get("offer_id");
  const ref = q.get("ref") || "DEMO-" + Math.random().toString(36).slice(2,8).toUpperCase();
  
  const [bookingData, setBookingData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);

  useEffect(() => {
    // Track the confirmation event
    supabase.functions.invoke('analytics-track', {
      body: { 
        event_type: "flight_booking_confirmed", 
        metadata: { amount, currency, provider, ref, leadId, offerId } 
      }
    }).catch(console.error);

    // Start polling for booking data
    if (offerId) {
      pollBookingData();
    } else {
      setIsLoading(false);
    }
  }, [amount, currency, provider, ref, leadId, offerId]);

  const pollBookingData = async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('offer_id', offerId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching booking:', error);
      } else if (data) {
        setBookingData(data);
        setIsLoading(false);
        return;
      }

      // Continue polling if no booking found and under 30 attempts (5 minutes)
      if (pollingCount < 30) {
        setPollingCount(prev => prev + 1);
        setTimeout(pollBookingData, 10000); // Poll every 10 seconds
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error polling booking data:', error);
      setIsLoading(false);
    }
  };

  const sendConfirmationEmail = async () => {
    if (!email.trim()) return;

    setSendingEmail(true);
    try {
      const { error } = await supabase.functions.invoke('send-confirmation-email', {
        body: {
          email: email.trim(),
          bookingReference: bookingData?.pnr || ref,
          amount,
          currency,
          offerId
        }
      });

      if (error) {
        console.error('Error sending email:', error);
        alert("Failed to send email. Please try again.");
      } else {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-xl text-center p-8 border border-border rounded-2xl card-shadow bg-card">
          <div className="w-16 h-16 mx-auto mb-4 gradient-bg rounded-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-heading font-semibold mb-2 text-flexaura-navy">
            We're confirming your booking...
          </h2>
          <p className="text-muted-foreground mb-4">
            Your installment payment has been authorized. We're issuing your tickets now and will email them shortly.
          </p>
          <div className="text-sm text-muted-foreground">
            This usually takes 1-3 minutes. Please don't close this page.
          </div>
        </div>
      </div>
    );
  }

  const hasTickets = bookingData?.pnr || bookingData?.tickets;
  const bookingReference = bookingData?.pnr || ref;
  const isConfirmed = bookingData?.status === 'confirmed';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-xl p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 gradient-bg rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-heading font-semibold mb-2 text-flexaura-navy">
            {hasTickets ? "Booking Confirmed!" : "Payment Successful!"}
          </h2>
          <p className="text-muted-foreground">
            {hasTickets 
              ? "Your flight tickets have been issued successfully."
              : "Your payment has been processed. We're finalizing your booking details."
            }
          </p>
        </div>

        <div className="border border-border rounded-2xl p-6 card-shadow bg-card space-y-4">
          <div>
            <div className="text-sm text-muted-foreground">Booking Reference</div>
            <div className="text-lg font-medium text-flexaura-navy">{bookingReference}</div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Amount</div>
              <div className="font-medium">{currency} {Number(amount).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Payment via</div>
              <div className="font-medium capitalize">{provider}</div>
            </div>
          </div>

          {bookingData?.status && (
            <div>
              <div className="text-sm text-muted-foreground">Status</div>
              <div className={`font-medium capitalize ${
                isConfirmed ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {bookingData.status}
              </div>
            </div>
          )}

          {!emailSent && (
            <div className="pt-4 border-t border-border">
              <div className="text-sm font-medium text-flexaura-navy mb-2">
                Get your tickets via email
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition"
                />
                <button
                  onClick={sendConfirmationEmail}
                  disabled={!email.trim() || sendingEmail}
                  className="px-4 py-2 gradient-bg text-white rounded-lg font-medium hover:opacity-90 smooth-transition disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            </div>
          )}

          {emailSent && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">Confirmation email sent to {email}</span>
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground pt-2">
            {hasTickets 
              ? "Your tickets and itinerary details have been processed. Check your email for the confirmation."
              : "If you don't receive an email within 30 minutes, contact hector@flexaura.me."
            }
          </div>
        </div>

        <div className="mt-6 flex gap-3 justify-center">
          <Link 
            to="/travel" 
            className="px-6 py-2 rounded-lg border border-border text-foreground hover:bg-accent smooth-transition"
          >
            Book Another Flight
          </Link>
          <Link 
            to="/" 
            className="px-6 py-2 gradient-bg text-white rounded-lg hover:opacity-90 smooth-transition"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}